import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import { toWhatsAppNumber } from "@/lib/media";
import EmailPreferences from "@/components/feature/EmailPreferences";

interface SettingsTabProps {
  userId: string;
  initialWhatsapp: string;
}

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

/**
 * Ajustes del vendedor: permite configurar el número de WhatsApp que se
 * mostrará en la ficha de sus publicaciones.
 */
export default function SettingsTab({
  userId,
  initialWhatsapp,
}: SettingsTabProps) {
  const [whatsapp, setWhatsapp] = useState(initialWhatsapp);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  const digits = toWhatsAppNumber(whatsapp);
  const preview = digits ? `https://wa.me/${digits}` : "";

  async function handleSave() {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ whatsapp: whatsapp.trim() || null })
        .eq("id", userId);
      if (error) throw error;
      showToast("Número de WhatsApp guardado correctamente.", "success");
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "No se pudo guardar el número.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-5 md:p-8">
      <div className="max-w-xl">
        <h2 className="font-heading text-lg font-bold text-foreground-950">
          Ajustes de contacto
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-foreground-600">
          Configura tu número de WhatsApp para que los compradores interesados
          puedan contactarte directamente desde tus publicaciones.
        </p>

        <div className="mt-6 space-y-5">
          <div>
            <label
              htmlFor="whatsapp"
              className="mb-1.5 block text-sm font-semibold text-foreground-800"
            >
              Número de WhatsApp
            </label>
            <div className="flex items-center gap-2">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100">
                <i className="ri-whatsapp-line text-xl text-secondary-600"></i>
              </span>
              <input
                id="whatsapp"
                type="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="Ej. +1 809 555 1234"
                className={inputClass}
              />
            </div>
            <p className="mt-1.5 text-xs text-foreground-400">
              Incluye el código de país para que el enlace funcione. Ejemplo: +1
              809 555 1234.
            </p>
            {preview && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-500">
                <i className="ri-links-line shrink-0"></i>
                <span className="min-w-0">
                  Enlace generado:{" "}
                  <span className="font-medium text-foreground-700">
                    {preview}
                  </span>
                </span>
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando…
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                Guardar número
              </>
            )}
          </button>
        </div>

        <div className="mt-10 border-t border-background-200/70 pt-8">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            Preferencias de email
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-foreground-600">
            Controlá qué correos comerciales querés recibir de VendeYa.
          </p>
          <div className="mt-5">
            <EmailPreferences userId={userId} />
          </div>
        </div>
      </div>
    </div>
  );
}